/**
 * Reading NWS and SPC text for the round-2 popups (Stage 10.5, 2d). Plain
 * TypeScript with no Svelte in it, so the tests call it directly.
 *
 * Nothing here produces markup. Every string it returns is interpolated by
 * Svelte as text, which is the whole defence against a third party's words.
 */

import { alertTone, type AlertTone } from './forecast/tone'

/**
 * NWS text arrives hard-wrapped at about 68 columns, with a blank line between
 * paragraphs ("* WHAT...", "* WHERE..."). Paragraphs are kept and the wrap
 * inside each is undone, so the popup sets it at its own measure rather than
 * as a ragged 68-column column inside a wider one.
 */
export function paragraphs(text: string | null | undefined): string[] {
  if (!text) return []
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s*\n\s*/g, ' ').trim())
    .filter((p) => p.length > 0)
}

/**
 * The level word and how loudly to draw it. The level is the event name's last
 * word ("Flash Flood Warning" → "Warning"), never `severity`, which is the
 * same rule the push poller follows. The tag is drawn in the alert's tier of
 * the condition palette (`alertTone`), so the popup, the row and the header
 * agree: yellow short of a Warning, red for one, deep red for an emergency.
 */
export function alertLevel(
  event: string | null | undefined,
  text?: string | null,
): { tag: string; tone: AlertTone } {
  const last = event?.trim().split(/\s+/).at(-1) ?? ''
  const tag = /^[A-Za-z]+$/.test(last) ? last : 'Alert'
  return { tag, tone: alertTone(event, text) }
}

/** NWS's `areaDesc` is one string of counties or zones joined by semicolons. */
export function areaList(area: string | null | undefined): string[] {
  if (!area) return []
  return area
    .split(';')
    .map((a) => a.trim())
    .filter((a) => a.length > 0)
}

/** The tail of a CAP id, for the footer: enough to tell two apart. */
export function capTail(id: string | null | undefined): string {
  if (!id) return ''
  const bare = id.replace(/^https?:\/\/[^/]+\/alerts\//, '')
  return bare.length > 28 ? `…${bare.slice(-24)}` : bare
}
